/**
 * Горизонтальные бары: сравнение категорий по ОДНОЙ величине.
 *
 * Форма выбрана до цвета и по работе читателя: он сравнивает величины у
 * названных единиц, а имена территорий и культур длинные — вертикальные
 * столбцы обрезали бы подписи, поэтому бары горизонтальные.
 *
 * Что закреплено формой:
 *
 * * **ряд ровно один.** Второй ряд рядом с первым означал бы сравнение двух
 *   разных величин в одной шкале; развилка «сделано / не сделано» показывается
 *   `DoneSplit`, а не второй полосой здесь. Поэтому и цвет один: раскрасить
 *   каждый бар «своим» оттенком значило бы второй раз закодировать длину бара;
 * * **величина стоит подписью у конца бара, снаружи.** Подпись внутри марки
 *   обрезалась бы на коротких барах — а обрезанное число хуже, чем никакое;
 * * **отсутствие величины — словами.** Ни нулевой полосы, ни пустой ячейки:
 *   «не считалось» и «ноль» — разные утверждения;
 * * **малый знаменатель рисуется числом, а не долей.** Доля от трёх контуров
 *   прыгает на 33 % от одной строки; такой бар сравнивать не с чем, и он не
 *   рисуется вовсе — на его месте стоит само число;
 * * **порядок объявлен вслух** подписью, а не подразумевается длиной баров.
 */

import { useRef, type ReactNode } from "react";
import { defineMessages } from "../i18n/define";
import { useT } from "../i18n/hooks";
import { ChartFrame, ChartTip, chartStyles as styles, useChartTooltip } from "./ChartFrame";

const m = defineMessages({
  notComputed: { id: "common.kit.bars.notComputed", defaultMessage: "величина не посчитана", description: "Вместо бара горизонтальной диаграммы, когда значения нет и причина не названа; строчная, узкое место справа от подписи" },
  barLabel: { id: "common.kit.bars.barLabel", defaultMessage: "{label}: {value}", description: "aria-label бара диаграммы для экранных дикторов; {label} — имя строки (хозяйство, район), {value} — готовая величина с единицей" },
  noteHeader: { id: "common.kit.bars.noteHeader", defaultMessage: "Оговорка", description: "Заголовок колонки таблицы-двойника диаграммы: пометка к строке (малый знаменатель, нет данных)" },
  tableSummary: { id: "common.kit.bars.tableSummary", defaultMessage: "Те же величины строками, включая те, у которых бара нет", description: "Подпись раскрывающейся таблицы под диаграммой: те же числа текстом, включая строки без бара" },
});

export interface BarDatum {
  key: string;
  /** Подпись строки: имя единицы, обычно ссылка на её уровень. */
  label: ReactNode;
  /** То же имя простым текстом — для таблицы, подсказки и озвучивания. */
  labelText: string;
  /**
   * Величина в единицах шкалы. `null` — рисовать нечего: причина обязана
   * стоять в `absent`.
   */
  value: number | null;
  /** Готовая подпись величины: «1 234,56», «менее 1%». Форматирует вызывающий. */
  display: string;
  /** Почему бара нет. Показывается вместо марки и повторяется в таблице. */
  absent?: string;
  /** Оговорка к строке: малый знаменатель, синтетика, пропуск источника. */
  note?: ReactNode;
}

/**
 * Один бар. Марка тонкая и не заполняет дорожку: остаток дорожки — воздух, а
 * не «недостающая часть величины».
 */
function BarRow({ row, max }: { row: BarDatum; max: number }) {
  const t = useT();
  const tooltip = useChartTooltip();
  const trackRef = useRef<HTMLDivElement>(null);

  const show = () => {
    const anchor = trackRef.current;
    if (!anchor) return;
    tooltip.show(
      <ChartTip value={row.display} label={row.labelText} note={row.note} />,
      anchor,
    );
  };

  if (row.value === null) {
    return (
      <li className={styles.barRow}>
        <span className={styles.barLabel}>{row.label}</span>
        <span className={styles.barAbsent}>
          {row.absent ?? t(m.notComputed)}
        </span>
        <span className={styles.barValue}>{row.display}</span>
        {row.note ? <span className={styles.barNote}>{row.note}</span> : null}
      </li>
    );
  }

  const width = max > 0 ? Math.max(0, Math.min(1, row.value / max)) * 100 : 0;

  return (
    <li className={styles.barRow}>
      <span className={styles.barLabel}>{row.label}</span>
      <div
        className={styles.barTrack}
        ref={trackRef}
        // Клавиатура получает ту же подсказку, что и мышь: величина не живёт
        // в наведении. Сама величина при этом уже подписана справа.
        tabIndex={0}
        onPointerEnter={show}
        onFocus={show}
        onPointerLeave={tooltip.hide}
        onBlur={tooltip.hide}
        aria-label={t(m.barLabel, { label: row.labelText, value: row.display })}
      >
        <div className={styles.barFill} style={{ width: `${width}%` }} />
      </div>
      <span className={styles.barValue}>{row.display}</span>
      {row.note ? <span className={styles.barNote}>{row.note}</span> : null}
    </li>
  );
}

export function Bars({
  title,
  unit,
  denominator,
  caption,
  epistemic,
  demo,
  data,
  /**
   * Предел шкалы. Для долей это 1 — иначе самый длинный бар всегда выглядел бы
   * как «сто процентов», каким бы малым ни было его значение.
   */
  max,
  maxDisplay,
  valueHeader,
}: {
  title: ReactNode;
  unit: string;
  denominator: string;
  caption: ReactNode;
  epistemic?: string | null;
  demo?: boolean;
  data: ReadonlyArray<BarDatum>;
  max?: number;
  /** Подпись предела шкалы. По умолчанию — само число. */
  maxDisplay?: string;
  /** Заголовок числовой колонки таблицы. */
  valueHeader: string;
}) {
  const t = useT();
  const values = data
    .map((row) => row.value)
    .filter((value): value is number => value !== null);
  const domain = max ?? (values.length > 0 ? Math.max(...values) : 0);
  const hasNotes = data.some((row) => row.note || row.absent);

  const table = (
    <table className={styles.table}>
      <thead>
        <tr>
          <th scope="col">{denominator}</th>
          <th scope="col" className={styles.num}>
            {valueHeader}
          </th>
          {hasNotes ? <th scope="col">{t(m.noteHeader)}</th> : null}
        </tr>
      </thead>
      <tbody>
        {data.map((row) => (
          <tr key={row.key}>
            <th scope="row">{row.label}</th>
            <td className={styles.num}>{row.display}</td>
            {hasNotes ? <td>{row.note ?? row.absent ?? ""}</td> : null}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <ChartFrame
      title={title}
      unit={unit}
      denominator={denominator}
      caption={caption}
      epistemic={epistemic}
      demo={demo}
      table={table}
      tableSummary={t(m.tableSummary)}
    >
      <ul className={styles.bars}>
        {data.map((row) => (
          <BarRow key={row.key} row={row} max={domain} />
        ))}
      </ul>
      {/* Ось подписывает только предел: остальные величины стоят у своих
          марок, и второй ряд одинаковых чисел был бы шумом. */}
      <div className={styles.axis}>
        <span className={styles.axisScale}>
          <span>0</span>
          <span>{maxDisplay ?? String(domain)}</span>
        </span>
      </div>
    </ChartFrame>
  );
}
